import { includes } from "lodash";
import { motion, type Variants } from "motion/react";
import { type PropsWithChildren, useEffect, useState } from "react";

import { Button } from "@krea/shared/design-system/Button";
import { cn } from "@krea/shared/utils";

import type { IApplyingCompanyExtended } from "@/features/lender-applications/types/applying-company";

const motionItem: Variants = {
  hidden: {
    height: "0px",
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
  visible: {
    height: "auto",
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
};

interface ICompanyStructureItemProps extends PropsWithChildren {
  item: NonNullable<IApplyingCompanyExtended["groupStructure"]>;
  expanded: boolean;
}

const CompanyStructureItem = ({
  item,
  expanded,
  children,
}: ICompanyStructureItemProps) => {
  const [isOpen, setIsOpen] = useState(!!expanded);

  useEffect(() => {
    setIsOpen(expanded);
  }, [expanded]);

  const orgName = item.organisationName;
  const orgNumber = item.organisationNumber;
  const countryCode = item.countryCode;

  const hasChildCompanies = item.children && item.children.length > 0;

  const toggleItem = () => {
    if (hasChildCompanies) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div
      className={cn("mt-4 rounded border px-4 py-4", {
        "border-gray-300  bg-gray-100": item.isCurrentCompany,
        "border-gray-300 bg-white": !item.isCurrentCompany,
      })}
    >
      <div
        className={cn("flex", hasChildCompanies && "cursor-pointer")}
        onClick={toggleItem}
      >
        {hasChildCompanies && (
          <div className="mr-3 shrink-0">
            <Button
              size="sm"
              variant="secondary"
              className="mt-1 h-6 w-6"
              onClick={toggleItem}
            >
              <div className="flex h-full w-full items-center justify-center">
                <span className="font-bold">{isOpen ? "-" : "+"}</span>
              </div>
            </Button>
          </div>
        )}
        <div>
          <div className="mb-1 text-sm font-bold">
            {`${orgName} (${orgNumber}), ${countryCode}`}
          </div>
          <div className="text-sm">
            {(() => {
              if (!item.ownedPercentage) {
                return item.isCurrentCompany ? "" : "Parent company";
              }

              return `Ownership: ${item.ownedPercentage}%`;
            })()}
          </div>
        </div>
      </div>

      {hasChildCompanies && (
        <motion.div
          variants={motionItem}
          initial="hidden"
          animate={isOpen ? "visible" : "hidden"}
          className="overflow-hidden"
        >
          <div className="pl-5">{children}</div>
        </motion.div>
      )}
    </div>
  );
};

interface IProps {
  companyStructure: IApplyingCompanyExtended["groupStructure"];
}

const CompanyStructure = ({ companyStructure }: IProps) => {
  const [isExpanded, setIsExpanded] = useState<string[]>([]);

  const findPathToCurrentCompany = (
    nodes: NonNullable<IApplyingCompanyExtended["groupStructure"]>[],
    path: string[] = [],
  ): string[] => {
    // Recursively find the path (org numbers) from the root to the node where is_current_company === true.
    // Only include the direct parent -> children org numbers to the is_current_company one and stop.
    // If not found, return empty array.
    for (const node of nodes) {
      const newPath = [...path, node.organisationNumber];

      if (node.isCurrentCompany) {
        return newPath;
      }

      if (node.children && node.children.length > 0) {
        const result = findPathToCurrentCompany(node.children, newPath);

        if (result.length > 0) {
          return result;
        }
      }
    }

    return [];
  };

  useEffect(() => {
    if (companyStructure) {
      // add root company to the initial group structures
      const initialGroupStructures = [companyStructure];

      if (companyStructure.children) {
        initialGroupStructures.push(...companyStructure.children);
      }
      const expandedOrgNums = findPathToCurrentCompany(
        initialGroupStructures,
        [],
      );

      setIsExpanded(expandedOrgNums);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createTree = (
    item: NonNullable<IApplyingCompanyExtended["groupStructure"]>,
    index: number,
  ) => {
    return (
      <CompanyStructureItem
        expanded={includes(isExpanded, item.organisationNumber)}
        key={index}
        item={item}
      >
        {item.children?.map((itm, index) => createTree(itm, index))}
      </CompanyStructureItem>
    );
  };

  if (!companyStructure) {
    return null;
  }

  return (
    <CompanyStructureItem item={companyStructure} expanded>
      {companyStructure.children?.map((item, index) => createTree(item, index))}
    </CompanyStructureItem>
  );
};

export { CompanyStructure };
